import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum CarrierPickupStatus {
  SCHEDULED = "Scheduled",
  CANCELLED = "Cancelled",
}

const PickupAddressSchema_ = z
  .object({
    companyName: z.string().nullish().meta({ privacy: privacy.nonPii }),
    contactName: z.string().nullish().meta({ privacy: privacy.person_name }),
    addressLine: z.string().nullish().meta({ privacy: privacy.redact }),
    room: z.string().nullish().meta({ privacy: privacy.redact }),
    floor: z.string().nullish().meta({ privacy: privacy.redact }),
    city: z.string().nullish().meta({ privacy: privacy.nonPii }),
    stateProvince: z.string().nullish().meta({ privacy: privacy.nonPii }),
    postalCode: z.string().nullish().meta({ privacy: privacy.nonPii }),
    countryCode: z.string().nullish().meta({ privacy: privacy.nonPii }),
    phoneNumber: z.string().nullish().meta({ privacy: privacy.phone }),
    phoneExtension: z.string().nullish().meta({ privacy: privacy.phone }),
  })
  .meta({ privacy: privacy.structured });

export const PickupAddressSchema: z.ZodType<PickupAddress> =
  PickupAddressSchema_;
export interface PickupAddress extends z.infer<typeof PickupAddressSchema_> {}

const TimeOfDaySchema_ = z
  .object({
    hour: z.number().min(0).max(23).meta({ privacy: privacy.nonPii }),
    minute: z.number().min(0).max(59).meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export const TimeOfDaySchema: z.ZodType<TimeOfDay> = TimeOfDaySchema_;
export interface TimeOfDay extends z.infer<typeof TimeOfDaySchema_> {}

const carrierPickupSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
    team: zExt.objectId().meta({ privacy: privacy.nonPii }),
    name: z.string().nullish().meta({ privacy: privacy.nonPii }),
    carrier: CarrierSchema,
    provider: createEnum(RateProvider, { enumName: "RateProvider" }).meta({
      privacy: privacy.nonPii,
    }),
    pickupRequestNumber: z.string().meta({ privacy: privacy.external_id }),
    pickupDate: z.date().meta({ privacy: privacy.nonPii }),
    readyTime: TimeOfDaySchema.nullish(),
    closeTime: TimeOfDaySchema.nullish(),
    pickupAddress: PickupAddressSchema.nullish(),
    packageCount: z.number().nullish().meta({ privacy: privacy.nonPii }),
    totalWeight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
    specialInstructions: z
      .string()
      .nullish()
      .meta({ privacy: privacy.free_text }),
    referenceNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
    status: createEnum(CarrierPickupStatus, { enumName: "CarrierPickupStatus" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    rate: z.string().nullish().meta({ privacy: privacy.nonPii }),
    carrierResponse: z
      .record(z.string(), z.unknown())
      .nullish()
      .meta({ privacy: privacy.null }),
    createdAt: z.date().meta({ privacy: privacy.nonPii }),
    updatedAt: z.date().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export const CarrierPickupSchema: z.ZodType<CarrierPickup> =
  carrierPickupSchema_;
export interface CarrierPickup extends z.infer<typeof carrierPickupSchema_> {}
